import { format } from "date-fns";
import ruLocale from "date-fns/locale/ru";
import { AttributeType, AttributeFormatDc } from "@evergis/api";
import { getDate } from "@evergis/react";
import { Coordinates } from "@evergis/sgis/es/baseTypes";

import { t } from "i18next";
import {
  TIME_ZONE_FORMAT,
  RU_LOCALE,
} from "../components/Dialogs/DialogComponents/Attributes/hooks/useUpdateAttributeFormat";

// eslint-disable-next-line max-len
import { getValueFromOptions } from "../components/Dialogs/DialogComponents/Attributes/AttributeFormat/FormStateSettings";
import { FeatureAttributeValue, JSONAttributeValue } from "../modules/FeatureCard/components/Attributes/types";
import { numberWithSpaces } from "./formatNumber";

import {
  dateOptions,
  timeOptions,
  pointOptions,
  numberOptions,
} from "../components/Dialogs/DialogComponents/Attributes/AttributeFormat/FormatSettingsOptions";

import { FeaturedDcExtendedGeometry } from "../ducks/selectFeatures/types";

import { i18n } from "../i18n";

const numberFormatTypes = [
  AttributeType.Int32,
  AttributeType.Int64,
  AttributeType.Double,
  AttributeType.MultiPolygon,
  AttributeType.Polyline,
];

const convertDDToDMS = (value: number) => {
  const absolute = Math.abs(value);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = Math.floor((minutesNotTruncated - minutes) * 60);

  // eslint-disable-next-line no-bitwise, @typescript-eslint/quotes, quotes
  return `${degrees}° ${minutes}' ${seconds}"`;
};

const formatPointValue = (stringFormat: AttributeFormatDc, value: Coordinates) => {
  if (stringFormat.format === pointOptions[1].value) {
    const lon = value[1];
    const lat = value[0];

    const lonDirection = lon >= 0 ? i18n.t("east", { ns: "common" }) : i18n.t("west", { ns: "common" });
    const latDirection = lat >= 0 ? i18n.t("north", { ns: "common" }) : i18n.t("south", { ns: "common" });

    return `${convertDDToDMS(lon)} ${lonDirection}, ${convertDDToDMS(lat)} ${latDirection}`;
  }

  if (stringFormat.format.includes("DD.") && value.length) {
    const rounding = stringFormat.format.replace("DD.", "").length;

    return `${value[0].toFixed(rounding)}, ${value[1].toFixed(rounding)}`;
  }

  return null;
};

const formatDateValue = (stringFormat: AttributeFormatDc, dateValue: number | string) => {
  const value = getDate(dateValue).getTime();
  const isRuLocale = stringFormat.culture && stringFormat.culture === RU_LOCALE;
  const showTimeZone = stringFormat.format.includes(TIME_ZONE_FORMAT);
  const dateOption = getValueFromOptions(dateOptions, stringFormat.format);
  const dateOptionValue = dateOption && dateOption.value;

  const dateFormat =
    value && dateOptionValue
      ? format(value as number, dateOptionValue, {
          locale: ruLocale,
        })
      : "";

  const timeOption = getValueFromOptions(timeOptions, stringFormat.format);
  const timeOptionValue = timeOption && (isRuLocale ? timeOption.value : `${timeOption.value} aa`);
  const timeFormat =
    (timeOptionValue && format(value as number, showTimeZone ? `${timeOptionValue} zz` : timeOptionValue)) || "";

  return dateFormat ? `${dateFormat} ${timeFormat}` : "";
};

const formatNumberValue = (
  stringFormat: AttributeFormatDc,
  value: number,
  type: AttributeType,
  noUnits = false,
): string => {
  const { scalingFactor, rounding, splitDigitGroup, unitsLabel } = stringFormat || {};
  const isIntValue = [AttributeType.Int32, AttributeType.Int64].includes(type);
  const isDefaultScaling = stringFormat && stringFormat.scalingFactor === +numberOptions[0].value;
  let currentValue: string | number = value;

  if (isIntValue && isDefaultScaling && currentValue) {
    currentValue = parseInt(currentValue.toString(), 10);
  }

  if (scalingFactor) {
    currentValue *= scalingFactor;
  }

  if ((rounding || rounding === 0) && (!isIntValue || (isIntValue && !isDefaultScaling))) {
    currentValue = currentValue && Number(currentValue).toFixed(rounding);
  }

  if (splitDigitGroup) {
    currentValue = numberWithSpaces(currentValue);
  }

  if (currentValue) {
    currentValue = currentValue.toString().replace(".", ",");
  }

  if (unitsLabel && !noUnits) {
    currentValue = `${currentValue} ${unitsLabel}`;
  }

  return currentValue?.toString() || "";
};

export const formatAttributeValue = (
  type: AttributeType,
  value: FeatureAttributeValue,
  stringFormat: AttributeFormatDc,
  noUnits = false,
): string => {
  if (type === AttributeType.Point) {
    if (!(stringFormat && stringFormat.format) || !value) return null;
    const { coordinates } = value as FeaturedDcExtendedGeometry;

    return formatPointValue(stringFormat, coordinates as Coordinates);
  }

  if (type === AttributeType.DateTime) {
    if (!(stringFormat && stringFormat.format) || !value) return null;

    return formatDateValue(stringFormat, value as number | string);
  }

  if (numberFormatTypes.includes(type) && !isNaN(value as number)) {
    return formatNumberValue(stringFormat, value as number, type, noUnits);
  }

  if (type === AttributeType.Json) {
    if (!value || (value as JSONAttributeValue[])?.length === 0) {
      return "";
    }

    return `${t("total", { ns: "table" })} ${(value as JSONAttributeValue[]).length}`;
  }

  return value ? value.toString() : "";
};
